import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { owaTrustedTypesPolicy } from '../trustedTypesPolicies/owaTrustedTypesPolicy';
import getGlobalSanitizerConfig from './getGlobalSanitizerConfig';
import { logUsage } from 'owa-analytics';
import { tryGetGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import LRUCache from './LRUCache';

enum SanitizerReturnType {
    String,
    HTMLElement,
    DocumentFragment,
    Node,
}

/**
 * This cache is used to store sanitized HTML strings to avoid re-sanitizing the same HTML multiple times.
 * The number can increase if we see more cases of HTML sanitizing.
 */
let sanitizedStringCache: LRUCache<string> | undefined;

/**
 * This cache is used to store html strings with style comments removed to avoid re-processing the same HTML multiple times.
 */
let styleCommentsCache: LRUCache<string> | undefined;

/**
 * Removes HTML comments **only inside `<style>` tags** so that DOMPurify does not
 * strip the CSS content they wrap, which would break styling.
 * Comments elsewhere in the document are left untouched to avoid un-commenting
 * intentionally deactivated markup.
 *
 * @example
 * removeStyleComments("<style><!--div { color: red }--></style>") returns "<style> div { color: red } </style>"
 */
function removeStyleComments(htmlString: string): string {
    // To avoid errors when htmlString is not a string, we check if it has the replace function before calling it.
    // If not, we log the usage and return the original input.
    if (typeof htmlString.replace !== 'function') {
        logUsage('TrustedTypesNonStringInput', tryGetGlobalSettingsAccountMailboxInfo(false), {
            inputType: typeof htmlString,
            isFalsy: !htmlString,
        });
        return htmlString;
    }

    // Match each <style ...>...</style> block and strip only the comment delimiters inside it
    return htmlString.replace(
        /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi,
        (_match, openTag: string, content: string, closeTag: string) =>
            openTag + content.replace(/<!--([\s\S]*?)-->/g, ' $1 ') + closeTag
    );
}

// Type mapping for return types
type ReturnTypeMap = {
    [SanitizerReturnType.String]: string;
    [SanitizerReturnType.HTMLElement]: HTMLElement;
    [SanitizerReturnType.DocumentFragment]: DocumentFragment;
    [SanitizerReturnType.Node]: Node;
};

// Function overloads using generics
function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T
): ReturnTypeMap[T];
function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T,
    extendedConfig: Config,
    optionalCacheID?: string
): ReturnTypeMap[T];

function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T,
    extendedConfig: Config = {},
    optionalCacheID?: string
): ReturnTypeMap[T] {
    if (!htmlString) {
        // If htmlString is not defined, default to empty string to avoid DOMPurify returning null
        htmlString = '';
    }

    // Create a unique cache key based on the HTML string and the serialized configuration
    const cacheKey = `${optionalCacheID ?? htmlString}:${JSON.stringify(
        extendedConfig
    )}:${JSON.stringify(returnType)}`;

    // Define one single instance of cache if is undefined
    if (sanitizedStringCache === undefined) {
        sanitizedStringCache = new LRUCache<string>(150);
    }

    // Define one single instance of styleCommentsCache if is undefined
    if (styleCommentsCache === undefined) {
        styleCommentsCache = new LRUCache<string>(150);
    }

    const cachedStyleComments = styleCommentsCache.get(cacheKey);

    if (cachedStyleComments) {
        htmlString = cachedStyleComments;
    } else {
        // Remove style-tag comments before sanitizing and cache the value
        const processed = removeStyleComments(htmlString);
        styleCommentsCache.set(cacheKey, processed);
        htmlString = processed;
    }

    const globalSanitizerConfig = getGlobalSanitizerConfig(extendedConfig);

    if (returnType === SanitizerReturnType.String) {
        // Check if the sanitized html string is in the cache
        const sanitizedStringValue = sanitizedStringCache.get(cacheKey);

        if (sanitizedStringValue) {
            return sanitizedStringValue as ReturnTypeMap[T];
        }

        const trustedHTML = owaTrustedTypesPolicy
            ? (owaTrustedTypesPolicy.createHTML(
                  htmlString,
                  globalSanitizerConfig
              ) as unknown as string)
            : DOMPurify.sanitize(htmlString, globalSanitizerConfig).toString();

        // Store the sanitized HTML string in the cache if is defined
        if (htmlString) {
            sanitizedStringCache.set(cacheKey, trustedHTML);
        }

        return trustedHTML as ReturnTypeMap[T];
    }

    if (returnType === SanitizerReturnType.DocumentFragment) {
        return DOMPurify.sanitize(htmlString, {
            ...globalSanitizerConfig,
            RETURN_DOM_FRAGMENT: true,
        }) as ReturnTypeMap[T];
    }

    // HTMLElement and Node both return a DOM element
    return DOMPurify.sanitize(htmlString, {
        ...globalSanitizerConfig,
        RETURN_DOM: true,
    }) as ReturnTypeMap[T];
}

export { getTrustedHTML, SanitizerReturnType };
